import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Search, Edit, Trash2, User, Mail, Phone, KeySquare } from 'lucide-react';
import constant from '../../../constant';

const AllUser = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);
    // const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);


    useEffect(() => {
        fetchAllUsers();
    }, []);

    useEffect(() => {
        const results = users.filter(user =>
            user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.mobile.includes(searchQuery)
        );
        setFilteredUsers(results);
    }, [searchQuery, users]);


    const fetchAllUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${constant.REACT_APP_API_URL}/user/all`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to fetch users. Status: ${response.status}`);
            }
            const data = await response.json();
            setUsers(data.data);
            setFilteredUsers(data.data);
            // console.log(data.data);
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };


    const handleCancel = () => {
        navigate(-1);
    };


    const formatISTDate = (utcDateString) => {
        const utcDate = new Date(utcDateString);
        const istDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
        return istDate.toLocaleDateString('en-IN', options);
    };

    const handleEditUser = (user) => {
        setUserToEdit(user);
        setIsEditing(true);
    };

    const handleEditFormCancel = () => {
        setIsEditing(false);
        setUserToEdit(null);
    };

    const handleEditSubmit = async (payload) => { // Receive payload instead of formData
        setEditLoading(true);
        setEditError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${constant.REACT_APP_API_URL}/user/${userToEdit._id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload), // Send payload in body
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to update user. Status: ${response.status}`);
            }
            fetchAllUsers();
            setIsEditing(false);
            setUserToEdit(null);
        } catch (err) {
            setEditError(err.message);
        } finally {
            setEditLoading(false);
        }
    };


    const handleDeleteUser = async (userId,activeStatus=false) => {
        // setDeleteLoading(true);
        // console.log(userId,activeStatus);
        
        setDeleteError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${constant.REACT_APP_API_URL}/user/${userId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ isActive: activeStatus }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to delete user. Status: ${response.status}`);
            }
            fetchAllUsers();
        } catch (err) {
            setDeleteError(err.message);
        } finally {
            // setDeleteLoading(false);
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return <div className="text-center py-10 text-red-500">{error}</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6 pt-16 md:pt-4">
            <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">

                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 transition-colors">
                                <ArrowLeft className="h-6 w-6" />
                            </button>
                            <h2 className="text-2xl font-bold text-gray-800">All Users</h2>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                size={20}
                            />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Edit User Form */}
                {isEditing && userToEdit && (
                    <div className="mb-6">
                        <EditUserForm
                            initialFormData={userToEdit}
                            onSubmit={handleEditSubmit}
                            onCancel={handleEditFormCancel}
                            loading={editLoading}
                            error={editError}
                        />
                    </div>
                )}

                {/* User Table */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    <div className="min-w-full overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Mobile
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created At (IST)
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user) => (
                                    <tr key={user._id} className="hover:bg-gray-50 transition-colors duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {user.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {user.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {user.mobile}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {formatISTDate(user.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => handleEditUser(user)}
                                                className="text-blue-600 hover:text-blue-800"
                                            >
                                                <Edit className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user._id, !user.isActive)}
                                                className={`${
                                                    user.isActive ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                                                } disabled:opacity-50`}
                                            >
                                                {user.isActive ? (
                                                    <Trash2 className="h-5 w-5" />
                                                ) : (
                                                    <User className="h-5 w-5" />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AllUser;


const EditUserForm = ({ initialFormData, onSubmit, onCancel, loading, error }) => {
    const [formData, setFormData] = useState({
        name: initialFormData.name,
        email: initialFormData.email,
        mobile: initialFormData.mobile,
        password: '',
    });

    useEffect(() => {
        setFormData({
            name: initialFormData.name,
            email: initialFormData.email,
            mobile: initialFormData.mobile,
            password: '',
        });
    }, [initialFormData]);


    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { // Create payload object
            name: formData.name,
            email: formData.email,
            mobile: formData.mobile,
        };
        if (formData.password) { // Conditionally add password
            payload.password = formData.password;
        }
        onSubmit(payload); // Send payload to onSubmit
    };

    return (
        <div className="bg-white rounded-lg shadow-sm">
            <div className="space-y-6 bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
                {error && <div className="text-red-500">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Name
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    <User className="h-4 w-4" />
                                </span>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    <Mail className="h-4 w-4" />
                                </span>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                                Mobile
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    <Phone className="h-4 w-4" />
                                </span>
                                <input
                                    type="tel"
                                    name="mobile"
                                    id="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password (Leave blank to keep current password)
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    <KeySquare className="h-4 w-4" />
                                </span>
                                <input
                                    type="password"
                                    name="password"
                                    id="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin inline-block" /> : 'Update User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};